import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Card from 'antd/lib/card'
import Badge from 'antd/lib/badge'
import guardianLoginBackground from '../assets/images/login.png'
import BSSLogo from '../components/Certificate/BSSLogo'
import { getQueryStringParams } from '../lib/QueryStringParams'
import ResetPassword from '../components/Auth/ResetPassword'
import NewPassword from '../components/Auth/NewPassword'
import Login from '../components/Auth/Login'
import { Auth } from 'aws-amplify';

import {
    setPasswordResetToken,
    setShowHelp,
    setShowLogin,
    setShowResetPassword,
    setShowNotifications,
    setUser,
    setShowNewPassword,
    setErrorMessage
} from '../redux/auth/actions'
import Help from '../components/Auth/Help'
import QuestionIcon from '../icons/Question'
import NotificationIcon from '../icons/Notifications'
import Notifications from '../components/Auth/Notifications'
import Overlay from '../components/Overlay'
import { SSO } from 'aws-sdk'
import SSOErrorPage from './SSOError'

const styles = {
    card: {
        background: 'rgba(255, 255, 255, 0.74)',
        borderRadius: '10px',
        padding: '0 0.32em',
    },
    formContainer: {
        position: 'absolute',
        left: '50%',
        margin: 'auto',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 480,
    },
    logoContainer: {
        margin: '10px auto',
        textAlign: 'center',
        width: 200,
    },
    submitButton: {
        width: 140,
    },
    ssoButton: {
        width: '100%',
        marginBottom: 5,
        background: '#52c41a',
        borderColor: '#52c41a'
    },
    error: {
        color: 'red',
        textAlign: 'center',
        fontSize: 14,
    },
    footer: {
        marginTop: '0.64em',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center'
    },
    buttons: {
        margin: '0.64em auto',
        display: 'flex',
        justifyContent: 'space-around',
        width: 90
    }
}

const AuthComponent = ({ authState, onStateChange }) => {
    const dispatch = useDispatch()

    const width = useSelector((state) => state.app.width)
    const showLogin = useSelector((state) => state.auth.showLogin)
    const showNewPassword = useSelector((state) => state.auth.showNewPassword)
    const showConfirmationForm = useSelector((state) => state.auth.showConfirmationForm)
    const showResetPassword = useSelector((state) => state.auth.showResetPassword)
    const [showLathamSSO, setShowLathamSSO] = useState(false)
    const [showVideo, setShowVideo] = useState(false)
    const [showSSOError, setShowSSOError] = useState(false)

    // Set up background
    useEffect(() => {
        document.body.style.backgroundImage = `url(${guardianLoginBackground})`
        document.body.style.backgroundPosition = 'left center'
        document.body.style.backgroundRepeat = 'no-repeat'
        document.body.style.backgroundSize = 'cover'
        document.body.style.backgroundColor = '#bdccd4'
        return () => {
            document.body.style.backgroundImage = null
            document.body.style.backgroundPosition = null
            document.body.style.backgroundRepeat = null
            document.body.style.backgroundSize = null
            document.body.style.backgroundColor = null
        }
    }, [])

    // Handle URL parameters and routing
    useEffect(() => {
        const search = window.location.search
        const pathname = window.location.pathname
        const paths = pathname.split('/')
        const searchParams = getQueryStringParams(search)

        if (paths[1] === 'reset-password' && searchParams['email'] && searchParams['token']) {
            // Handle admin password reset link with email and temp password - process automatically
            handlePasswordResetLink(searchParams['email'], searchParams['token'])
        } else if (paths[1] === 'reset-password' && searchParams['email'] && searchParams['code']) {
            // Handle forgot password link with email and verification code - show reset form
            setShowResetPassword(true)
            // The ResetPassword component will handle the URL parameters
        } else if (searchParams['reset']) {
            setShowResetPassword(true)
            setPasswordResetToken(searchParams['reset'])
        } else if (searchParams['confirm']) {
            showConfirmationForm(searchParams['confirm'], searchParams['code'], false)
        } else if ((paths[1] === 'invite' || paths[1] === 'registration') && paths[2]) {
            onStateChange('signUp', { token: paths[2] })
        } else if (paths[1] === 'login' && paths[2] === 'latham') {
            setShowLathamSSO(true)
            setShowLogin(true)
        } else if(searchParams['error_description']) {
            setShowSSOError(true)
        } else {
            setShowLogin(true)
        }
    }, [])

    const handlePasswordResetLink = async (email, tempPassword) => {
        try {
            setErrorMessage('')
            // Sign in with the temporary password from the URL
            const user = await Auth.signIn(email.toLowerCase(), tempPassword)

            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                // Set the user and show new password form directly
                setUser(user)
                setShowLogin(false)
                setShowNewPassword(true)
            } else if (user && !user.challengeName) {
                // User is already signed in, redirect to app
                onStateChange('signedIn', user)
            } else {
                setShowLogin(true)
            }
        } catch (error) {
            // Show Cognito reason instead of silently falling back to login.
            const errorText = error?.message || 'Unable to process password reset link.'
            setErrorMessage(`Password reset link error: ${errorText}`)
            setShowLogin(true)
        }
    }

    const containerStyle = width < 576 ? { ...styles.formContainer, width: '86%' } : styles.formContainer

    if(showSSOError) {
        return (
            <SSOErrorPage />
        )
    }

    // If not in signIn or signedIn state, don't render anything
    if (authState !== 'signIn' && authState !== 'signedIn') return null;

    return (
        <>
            {authState === 'signIn' && (
                <>
                    <div style={styles.logoContainer}>
                        <svg
                            version="1.1"
                            viewBox="0 0 126.17 53.72"
                            width="100%"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <BSSLogo />
                        </svg>
                    </div>

                    <div style={containerStyle}>
                        <Card style={styles.card}>
                            {showLogin && !showNewPassword && !showResetPassword && (
                                <Login
                                    showLathamSSO={showLathamSSO}
                                    onStateChange={onStateChange}
                                />
                            )}
                            {showNewPassword && (
                                <NewPassword onStateChange={onStateChange} />
                            )}
                            {showResetPassword && (
                                <ResetPassword onStateChange={onStateChange} onClose={() => setShowResetPassword(!showResetPassword)} />
                            )}
                        </Card>
                        <div style={styles.buttons}>
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowHelp(true)}
                            >
                                <QuestionIcon height={32} />
                            </span>
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowNotifications(true)}
                            >
                                <Badge>
                                    <NotificationIcon height={32} />
                                </Badge>
                            </span>
                        </div>
                    </div>
                    <Help />
                    <Notifications />
                    <Overlay
                        visible={showVideo}
                        headerText={'BSS Guardian Announcement'}
                        onClose={() => setShowVideo(false)}
                        closebutton={true}
                    >
                        <video autoPlay controls width="650">
                            <source src="https://bss-demo-videos.s3.us-west-1.amazonaws.com/bssguardianannouncement/Login-new.mp4" type="video/mp4" />
                        </video>
                    </Overlay>
                </>
            )}
        </>
    );
}

export default AuthComponent
